/**
 * SurpriseBadge Component
 *
 * Displays the surprise badge with a number overlay showing how many have been earned.
 * Used in BadgeGrid and potentially other places to show engagement achievements.
 */
import React from 'react';
import { View, Image, StyleSheet } from 'react-native';
import CustomText from './CustomText';
import AppStyles from '../AppStyles';
import { BADGE_PLACEHOLDER } from '../util/badgeUtils';
import { useTranslation } from '../i18n';

/**
 * SurpriseBadge Component
 *
 * @param {Object} props
 * @param {number} props.count - Number of surprise badges earned (0 = locked state)
 * @param {number} props.size - Badge size (default 80)
 * @param {boolean} props.showLabel - Whether to show "Surprise" label below
 * @param {boolean} props.compact - Compact mode for smaller displays
 */
const SurpriseBadge = ({ count = 0, size = 80, showLabel = true, compact = false }) => {
  const { t } = useTranslation('badges');
  const isEarned = count > 0;
  const imageSize = compact ? 60 : size;

  return (
    <View
      style={[styles.container, compact && styles.containerCompact]}
      accessibilityRole="image"
      accessibilityLabel={
        isEarned
          ? t('bonusBadgeLabel', { count })
          : t('mysteryBadgeLabel')
      }
    >
      <View style={styles.imageWrapper}>
        <Image
          source={BADGE_PLACEHOLDER}
          style={[
            styles.badgeImage,
            { width: imageSize, height: imageSize },
            !isEarned && styles.badgeLocked,
          ]}
        />

        {/* Number overlay - centered on badge like the "?" */}
        {isEarned && (
          <View style={styles.numberOverlay}>
            <CustomText font="bold" size={compact ? 20 : 28} color="#8B5CF6">
              {count > 99 ? '99+' : count}
            </CustomText>
          </View>
        )}

        {/* Lock overlay for unearned */}
        {!isEarned && (
          <View style={styles.lockOverlay}>
            <CustomText font="medium" size={20} color="#9CA3AF">
              ?
            </CustomText>
          </View>
        )}
      </View>

      {showLabel && (
        <View style={styles.labelContainer}>
          <CustomText
            font={isEarned ? 'semiBold' : 'regular'}
            size={compact ? 11 : 13}
            color={isEarned ? AppStyles.colorSet.textPrimary : AppStyles.colorSet.textMuted}
            style={styles.label}
          >
            {isEarned ? t('bonusLabel') : '?'}
          </CustomText>
          {isEarned && !compact && (
            <CustomText
              font="regular"
              size={11}
              color={AppStyles.colorSet.textMuted}
              style={styles.sublabel}
            >
              {t('bonusEarnedCount', { count })}
            </CustomText>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    padding: 8,
  },
  containerCompact: {
    padding: 4,
  },
  imageWrapper: {
    position: 'relative',
  },
  badgeImage: {
    resizeMode: 'contain',
  },
  badgeLocked: {
    opacity: 0.4,
  },
  numberOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 12, // Match lockOverlay offset for badge ribbon
    justifyContent: 'center',
    alignItems: 'center',
  },
  lockOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 12, // Offset up to account for badge ribbon
    justifyContent: 'center',
    alignItems: 'center',
  },
  labelContainer: {
    marginTop: 8,
    alignItems: 'center',
  },
  label: {
    textAlign: 'center',
  },
  sublabel: {
    textAlign: 'center',
    marginTop: 2,
  },
});

export default SurpriseBadge;
